import { getCountry, getCurrencyMeta, type CurrencyCode } from '@/data/locales';

let _currency: CurrencyCode | string = 'GBP';
let _intlLocale = 'en-GB';

/** Call when company profile/settings load so formatters use company currency. */
export function setLocaleContext(opts: {
  currency?: string | null;
  country?: string | null;
  uiLanguage?: string | null;
}) {
  const country = getCountry(opts.country);
  // Empty currency always falls back to GBP (UK default), not Pakistan.
  const rawCurrency = String(opts.currency || '').trim().toUpperCase();
  _currency = rawCurrency || 'GBP';
  const uiLang = opts.uiLanguage || 'en';
  if (uiLang === 'ar' || uiLang === country.localLanguage) {
    _intlLocale = country.intlLocale;
  } else {
    const region = country.intlLocale.split('-')[1] || country.code || 'GB';
    _intlLocale = `en-${region}`;
  }
}

export function getActiveCurrency(): string {
  return _currency || 'GBP';
}

export function getActiveIntlLocale(): string {
  return _intlLocale || 'en-GB';
}

export function getCurrencySymbol(currency?: string | null): string {
  return getCurrencyMeta(currency || _currency).symbol;
}

const THREE_DECIMAL_CURRENCIES = new Set(['BHD', 'OMR', 'KWD']);

export function getMoneyDecimals(currency?: string | null): number {
  const c = String(currency || _currency || 'GBP').toUpperCase();
  return THREE_DECIMAL_CURRENCIES.has(c) ? 3 : 2;
}

/** Round monetary values to currency-appropriate decimal places (2 for most, 3 for BHD/OMR/KWD). */
export function roundMoney(amount: number | null | undefined, currency?: string | null): number {
  const n = Number(amount) || 0;
  const decimals = getMoneyDecimals(currency);
  const factor = 10 ** decimals;
  return Math.round((n + Number.EPSILON) * factor) / factor;
}

/** Line total: qty × rate, rounded for display and totals. */
export function calcLineAmount(qty: number, rate: number, currency?: string | null): number {
  return roundMoney(qty * rate, currency);
}

/** Official Saudi Riyal sign (Unicode U+20C1), approved by SAMA Feb 2025 */
export const SAR_SYMBOL = '\u20C1';

function formatAmount(n: number, locale: string, decimals: number): string {
  // Always show currency decimals (SD-UK-041/056) — avoid £2 for £2.00 looking like rounded £1.99
  return n.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatMoney(
  amount: number | null | undefined,
  opts?: { currency?: string | null; locale?: string | null; showSymbol?: boolean },
): string {
  const currency = String(opts?.currency || _currency || 'GBP').toUpperCase();
  const n = roundMoney(Number(amount) || 0, currency);
  const meta = getCurrencyMeta(currency);
  const symbol = currency === 'SAR' ? SAR_SYMBOL : meta.symbol;
  const locale = opts?.locale || _intlLocale || 'en-GB';
  const decimals = THREE_DECIMAL_CURRENCIES.has(currency) ? 3 : 2;
  const formatted = formatAmount(n, locale, decimals);
  if (opts?.showSymbol === false) return formatted;
  // GCC Arabic locales: official riyal sign typically precedes the amount (SAMA guidance)
  if (currency === 'SAR' && locale.startsWith('ar')) {
    return `${symbol}\u00A0${formatted}`;
  }
  return `${symbol} ${formatted}`;
}

/** Parse YYYY-MM-DD as a local calendar date (avoids UTC midnight off-by-one). */
export function parseCalendarDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const s = String(value).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  // SD-UK-034: any YYYY-MM-DD… (date-only, space, or T) → local calendar day (avoid UTC −1)
  if (m && (s.length === 10 || s[10] === 'T' || s[10] === ' ')) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local calendar yyyy-MM-dd (never use toISOString for tenant-local dates). */
export function toLocalDateString(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function formatDate(value: string | Date | null | undefined, opts?: { locale?: string | null }): string {
  if (!value) return '';
  const d = parseCalendarDate(value);
  if (!d) return String(value);
  const locale = opts?.locale || _intlLocale || 'en-GB';
  return d.toLocaleDateString(locale);
}
